import { useRemoteSelect } from '@neo/hooks/use-remote-select'
import { Label, OptionalLabel } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { useCallback, useMemo, useRef } from 'react'
import {
  isSubaccountDisabled,
  SUBACCOUNT_PAGE_SIZE,
  SUBACCOUNT_SEARCH_PARAM,
} from 'v2/shared/hooks/use-all-subaccounts'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RSubaccount, RSubaccounts } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import {
  INITIAL_PAIRING,
  type PairingState,
  reconcileSavedSelection,
  seedLabelForValue,
  withSelectedSubaccountOption,
} from './subaccount-selected-option'

export interface SubAccountsSelectProps {
  label?: string
  value: string
  onChange: (value: string | string[]) => void
  placeholder?: string
  valueField?: 'auth_id' | 'account_id'
  disabled?: boolean
  clearable?: boolean
  /**
   * Name for the saved selection, for callers whose value cannot be resolved
   * server-side — `account_id` mode, since getSubaccount is auth_id-keyed and
   * the list API ignores auth_id/account_id/id filters. Only pass a name keyed
   * to THIS value (e.g. the record's own subaccount_name); a name belonging to
   * a different field would label the option wrongly.
   */
  selectedOptionLabel?: string
  /**
   * When true, a disabled subaccount is listed greyed out and cannot be picked — this is
   * a NEW-selection source (e.g. the flow tagging dropdown), so a disabled SA shouldn't be
   * chosen going forward. The CURRENTLY selected value stays pickable even if it points at
   * a disabled SA, so an existing selection keeps rendering its name instead of going blank.
   * @default false
   */
  excludeDisabled?: boolean
}

// Default "no subaccount" option matching phlo-console behavior
const NO_SUBACCOUNT_OPTION = { label: '----', value: 'Nil' }

/**
 * Whether a subaccount is listed but not pickable. Under `excludeDisabled` (a NEW-selection
 * source, e.g. the flow tagging dropdown) a disabled subaccount stays in the list, greyed out,
 * rather than being dropped: the options arrive a page at a time, and the Select only wires
 * its load-more once a page renders enough rows, so dropping the disabled ones could leave a
 * short page with no way to reach the rest. The CURRENT selection is always pickable, so an
 * existing tag keeps rendering its name. A pure function so the rule is unit-testable without
 * opening the Select (whose Radix popover doesn't open under happy-dom).
 */
export const isSubaccountUnpickable = (
  subaccount: RSubaccount,
  {
    excludeDisabled,
    currentValue,
    valueField,
  }: { excludeDisabled: boolean; currentValue: string; valueField: 'auth_id' | 'account_id' },
): boolean => {
  if (!excludeDisabled || !isSubaccountDisabled(subaccount)) return false
  const optionValue =
    valueField === 'account_id' ? String(subaccount.account_id) : subaccount.auth_id
  return optionValue !== currentValue
}

export const DISABLED_SUBACCOUNT_TOOLTIP = 'This sub account is disabled and cannot be selected.'

/**
 * The label an option renders with. Under `excludeDisabled` a disabled subaccount is
 * marked, whether it is the greyed-out entry or the current selection an already-tagged
 * flow still points at. Consumers that don't exclude disabled subaccounts (endpoints,
 * applications) keep plain names, unchanged.
 */
export const subaccountOptionLabel = (subaccount: RSubaccount, excludeDisabled: boolean): string =>
  excludeDisabled && isSubaccountDisabled(subaccount)
    ? `${subaccount.name} (disabled)`
    : subaccount.name

export const SubAccountsSelect = ({
  label,
  value,
  onChange,
  placeholder = 'Associate endpoint to a sub account',
  valueField = 'auth_id',
  disabled = false,
  clearable = false,
  selectedOptionLabel,
  excludeDisabled = false,
}: SubAccountsSelectProps) => {
  const toValue = (sub: RSubaccount) =>
    valueField === 'account_id' ? String(sub.account_id) : sub.auth_id

  const toOption = (sub: RSubaccount) => {
    const unpickable = isSubaccountUnpickable(sub, {
      excludeDisabled,
      currentValue: value,
      valueField,
    })
    return {
      label: subaccountOptionLabel(sub, excludeDisabled),
      // Search on the bare name so typing still matches a "(disabled)" entry
      searchValue: sub.name,
      value: toValue(sub),
      disabled: unpickable,
      tooltipContent: unpickable ? DISABLED_SUBACCOUNT_TOOLTIP : undefined,
    }
  }

  // Only auth_id selections can be resolved when they fall outside the loaded
  // pages — getSubaccount is keyed on auth_id, and the list API ignores an
  // auth_id/account_id filter, so account_id mode has no lookup available.
  const resolvableValue =
    valueField === 'auth_id' && value && value !== NO_SUBACCOUNT_OPTION.value ? value : undefined

  // A failed load renders as an empty list, indistinguishable from "this
  // account has no subaccounts" — say so instead. Reported from the failing
  // fetch itself rather than from an effect watching the hook's `error`: the
  // fetch rejecting IS the event, and watching the derived state costs an extra
  // render and reports late (react-doctor/no-event-handler).
  //
  // The guard is for retries, not for renders — React Query retries a failed
  // query, and each attempt runs this. One toast per outage, re-armed by the
  // next success.
  const reportedFailure = useRef(false)
  const fetchPage = useCallback(async (params: Record<string, unknown>) => {
    try {
      const response = await settingsService.listSubaccountsInternal(params)
      reportedFailure.current = false
      return response
    } catch (error) {
      if (!reportedFailure.current) {
        reportedFailure.current = true
        logger.error('Failed to load subaccounts', error)
        errorToast({
          title: 'Failed to load subaccounts',
          description: 'The list may be incomplete. Please try again.',
        })
      }
      throw error
    }
  }, [])

  const { options, setSearchTerm, isLoading, loadMore } = useRemoteSelect({
    queryKey: [...getAPIConsoleQueryKeys().subaccounts, 'form-select', valueField],
    enableOffsetMode: true,
    pageSize: SUBACCOUNT_PAGE_SIZE,
    searchKey: SUBACCOUNT_SEARCH_PARAM,
    fetchService: fetchPage,
    optionExtractor(data) {
      const subaccounts = data as RSubaccounts
      return {
        options: (subaccounts.objects || []).map(toOption),
        hasNextPage: !!subaccounts.meta?.next,
      }
    },
    selectedValue: resolvableValue,
    fetchResource: (id) => settingsService.getSubaccount(id as string),
    optionFromResource(data) {
      // The resolved row is the CURRENT selection, so it always renders — under
      // excludeDisabled a disabled one is marked rather than dropped.
      return toOption(data as RSubaccount)
    },
  })

  // Pin the caller's label to the value it arrived with. selectedOptionLabel
  // names the SAVED subaccount, so once the user picks a different out-of-page
  // one it would otherwise print the old name against the new value — the
  // failure mode is a convincing wrong name, not a blank. The pairing decision
  // itself lives in reconcileSavedSelection, which documents why a parent
  // hydration and a user selection must be handled differently.
  const pairing = useRef<PairingState>(INITIAL_PAIRING)
  const changedByUser = useRef(false)

  const reconciled = reconcileSavedSelection(
    pairing.current,
    { value, label: selectedOptionLabel },
    changedByUser.current,
  )
  // Clear the flag only once the change it describes has actually arrived — an
  // unrelated re-render between the pick and the parent's update would
  // otherwise consume it, and the pick would then read as a hydration.
  if (reconciled !== pairing.current) changedByUser.current = false
  pairing.current = reconciled

  const seedLabel = seedLabelForValue(pairing.current.saved, value)

  const handleChange = (next: string | string[]) => {
    changedByUser.current = true
    onChange(next)
  }

  // account_id mode has no server-side lookup, so keep the saved selection
  // visible from the name its own record carries. Skipped once the value shows
  // up in a loaded page, and never used in auth_id mode where fetchResource
  // already returns the authoritative name.
  const selectOptions = useMemo(() => {
    const withSelection = withSelectedSubaccountOption(options, {
      value,
      selectedOptionLabel: seedLabel,
      sentinelValue: NO_SUBACCOUNT_OPTION.value,
    })
    // When clearable, don't prepend the "----" / Nil option
    return clearable ? withSelection : [NO_SUBACCOUNT_OPTION, ...withSelection]
  }, [options, seedLabel, value, clearable])

  return (
    <Select
      label={
        label ? (
          <Label className="block mb-1">{label}</Label>
        ) : (
          <OptionalLabel className="block mb-1">Subaccount</OptionalLabel>
        )
      }
      clearable={clearable}
      options={selectOptions}
      placeholder={placeholder}
      className="w-full"
      value={value}
      onChange={handleChange}
      loading={isLoading}
      searchable
      onSearchChange={setSearchTerm}
      onLoadMore={loadMore}
      data-testid="select-subaccounts"
      disabled={disabled}
    />
  )
}
